import { useEffect, useRef } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box, Container, Typography } from "@mui/material";
import ExportHeader from "../components/impact/ExportHeader";
import StatsOverview from "../components/impact/StatsOverview";
import AgeDistribution from "../components/impact/AgeDistribution";
import AgenciesEngaged from "../components/impact/AgenciesEngaged";
import AgencyCoverage from "../components/impact/AgencyCoverage";
import QualifiedSavings from "../components/impact/QualifiedSavings";
import InterviewsConducted from "../components/impact/InterviewsConducted";
import VideosUploaded from "../components/impact/VideosUploaded";
import { DashboardData, FilterState } from "../types";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const theme = createTheme({
  palette: {
    primary: {
      main: "#592b7b",
      light: "#8B5CF6",
      dark: "#553C9A",
    },
    secondary: {
      main: "#3B82F6",
      light: "#60A5FA",
      dark: "#2563EB",
    },
    background: {
      default: "#F8FAFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1E293B",
      secondary: "#64748B",
    },
  },
  typography: {
    fontFamily: '"Lato"',
    h4: {
      fontWeight: 700,
      fontSize: "1.75rem",
      lineHeight: 1.2,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: "none",
          border: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          // Only apply paddingBottom: 0 for export components
          "&.export-card-content": {
            paddingBottom: 0,
          },
        },
      },
    },
  },
});

function ExportImpact({
  mapState,
  filters,
  useVerbiage,
  stateOrOrganization,
  setLoading,
  setIsExportMode,
  dashboardData,
  centerName,
}: {
  mapState: string;
  filters: FilterState;
  useVerbiage: "state" | "organization" | "nationwide";
  stateOrOrganization: string;
  setLoading: (loading: boolean) => void;
  setIsExportMode: (isExportMode: boolean) => void;
  dashboardData: DashboardData;
  centerName: string;
}) {
  const hasExported = useRef(false);

  const getFileName = () => {
    const date = new Date();
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    if (filters.showOrgData) {
      return `${centerName} Impact Overview ${formattedDate}.pdf`;
    }
    if (stateOrOrganization) {
      return `${stateOrOrganization} Impact Overview ${formattedDate}.pdf`;
    } else {
      return `Nationwide Impact Overview ${formattedDate}.pdf`;
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (hasExported.current) {
        return;
      }

      hasExported.current = true;
      const element = document.getElementById("page-content");

      if (!element) {
        console.error("impact-overview-page element not found");
        setIsExportMode(false);
        return;
      }

      setTimeout(async () => {
        const canvas = await html2canvas(element);
        const imgData = canvas.toDataURL("image/jpeg");
        const pdf = new jsPDF("l", "px", [777, 500]);
        const imgWidth = 777;
        const imgHeight = 500;

        pdf.addImage(
          imgData,
          "JPEG",
          0,
          0,
          imgWidth,
          imgHeight,
          undefined,
          "FAST"
        );

        pdf.save(getFileName());
        setIsExportMode(false);
        setLoading(false);
      }, 1000);
    })();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box
        id="page-content"
        sx={{
          height: "816px",
          width: "1240px",
          bgcolor: "background.default",
          background: "white",
        }}
      >
        <Container sx={{ width: "100%", m: 0, p: 0 }}>
          <ExportHeader
            stateOrOrganization={stateOrOrganization}
            organization={centerName}
            useVerbiage={useVerbiage}
          />

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "540px 600px",
                maxHeight: "150px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{ fontWeight: 1000, color: "primary.main" }}
                >
                  IMPACT REPORT
                </Typography>
                <Typography
                  variant="body1"
                  component="p"
                  sx={{ fontWeight: 400, color: "text.secondary" }}
                >
                  Forwarding the Mission of Child Protection
                </Typography>
              </Box>
              <StatsOverview data={dashboardData.stats} isExport={true} />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "4fr 4fr 5fr",
                height: "290px",
              }}
            >
              <InterviewsConducted
                data={dashboardData.interviewsConducted}
                useVerbiage={useVerbiage}
                stateOrOrganization={stateOrOrganization}
                isExport={true}
              />
              <AgeDistribution
                data={dashboardData.ageDistribution}
                useVerbiage={useVerbiage}
                stateOrOrganization={stateOrOrganization}
                isExport={true}
              />

              <AgencyCoverage
                data={dashboardData.agencyCoverage}
                mapState={mapState}
                isExport={true}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "3fr 3fr 5fr",
                gap: 3,
                height: "290px",
                pt: 3,
              }}
            >
              <VideosUploaded
                data={dashboardData.videosUploaded}
                useVerbiage={useVerbiage}
                stateOrOrganization={stateOrOrganization}
                isExport={true}
              />
              <AgenciesEngaged
                data={dashboardData.agenciesEngaged}
                isExport={true}
              />

              <QualifiedSavings
                data={dashboardData.qualifiedSavings}
                useVerbiage={useVerbiage}
                stateOrOrganization={stateOrOrganization}
                isExport={true}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default ExportImpact;
